import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router";
import t from "@/lang/lang";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import { Label } from "@/shared/components/ui/label";
import { Textarea } from "@/shared/components/ui/textarea";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import {
  useQpmOfferChangeDocumentProfileMutation,
  useQpmOfferReloadOfferConfigSnapshotMutation,
  useQpmOfferShowQuery,
} from "@/services/backend/qpm/offers/api/offer-api";
import { ChangeOfferDocumentProfileRequest } from "@/services/backend/qpm/offers/types/offer";
import { ChangeMode } from "@/services/backend/qpm/offers/types/offer-enum";
import {
  deriveTargetOrganismOptionsFromMonitorSnaps,
  TargetOrganismSelectionEditor,
  type TargetOrganismSelectionValue,
  isSameTargetOrganismSelection,
  normalizeTargetOrganismSelectionValue,
} from "@/shared/components/domain/qpm/target-organism-selection-editor";
import { useDebouncedMutationWithPersistenceStateContextUpdate } from "@/shared/lib/debounce/debounce";
import {
  rtkErrIsValidationError,
  ValidationError,
} from "@/shared/lib/errors/validation-error";
import { useOfferContext } from "@/routes/processes/[processId]/qpm/offers/[offerId]/_components/offer-context";

function buildPestsRequest({
  offerId,
  selection,
  savedSelection,
  customText,
  savedCustomText,
}: {
  offerId: string;
  selection: TargetOrganismSelectionValue;
  savedSelection: TargetOrganismSelectionValue;
  customText: string;
  savedCustomText: string;
}): ChangeOfferDocumentProfileRequest {
  const next: ChangeOfferDocumentProfileRequest = {
    offerId,
    documentProfile: {},
  };

  const normalizedCustomText = customText.trim();
  const hasSelectionChange = !isSameTargetOrganismSelection(
    savedSelection,
    selection,
  );
  const hasCustomTextChange = savedCustomText.trim() !== normalizedCustomText;

  if (hasSelectionChange || hasCustomTextChange) {
    next.targetOrganismSelection = {
      mode: ChangeMode.Manual,
      categoryKeys: [...selection.categoryKeys],
      typeKeys: [...selection.typeKeys],
      customText: normalizedCustomText,
    };
  }

  return next;
}

export function OfferPestsRoute() {
  const params = useParams();
  const { offerId } = params;
  const { setValidationError } = useOfferContext();

  const {
    data: offer,
    isLoading,
    error,
  } = useQpmOfferShowQuery({ id: offerId! }, { skip: !offerId });

  const [
    changeDocumentProfile,
    {
      error: changeDocumentProfileError,
      isLoading: isSavingDocumentProfile,
      isSuccess: isChangeDocumentProfileSuccess,
      reset: resetChangeDocumentProfile,
    },
  ] = useQpmOfferChangeDocumentProfileMutation();
  const [reloadSnapshot] = useQpmOfferReloadOfferConfigSnapshotMutation();

  const initialSelection = useMemo<TargetOrganismSelectionValue>(
    () =>
      normalizeTargetOrganismSelectionValue({
        categoryKeys: offer?.targetOrganismSelection?.categoryKeys ?? [],
        typeKeys: offer?.targetOrganismSelection?.typeKeys ?? [],
      }),
    [
      offer?.targetOrganismSelection?.categoryKeys,
      offer?.targetOrganismSelection?.typeKeys,
    ],
  );

  const [selection, setSelection] =
    useState<TargetOrganismSelectionValue>(initialSelection);
  const [savedSelection, setSavedSelection] =
    useState<TargetOrganismSelectionValue>(initialSelection);
  const [customText, setCustomText] = useState<string>(
    offer?.targetOrganismSelection?.customText ?? "",
  );
  const [savedCustomText, setSavedCustomText] = useState<string>(
    offer?.targetOrganismSelection?.customText ?? "",
  );
  const [savedRequest, setSavedRequest] =
    useState<ChangeOfferDocumentProfileRequest | null>(null);

  const { categoryOptions, typeOptions } = useMemo(
    () => deriveTargetOrganismOptionsFromMonitorSnaps(offer?.monitorSnaps),
    [offer?.monitorSnaps],
  );

  const initializedStateKey = useRef<string | null>(null);
  const lastSubmittedRequest = useRef<ChangeOfferDocumentProfileRequest | null>(
    null,
  );

  useEffect(() => {
    if (!offer) {
      return;
    }

    const nextStateKey = JSON.stringify({
      offerId: offer.id,
      selection: initialSelection,
      customText: offer.targetOrganismSelection?.customText ?? "",
    });

    if (initializedStateKey.current === nextStateKey) {
      return;
    }

    initializedStateKey.current = nextStateKey;
    const initialCustomText = offer.targetOrganismSelection?.customText ?? "";
    setSelection(initialSelection);
    setSavedSelection(initialSelection);
    setCustomText(initialCustomText);
    setSavedCustomText(initialCustomText);
    setSavedRequest(
      buildPestsRequest({
        offerId: offer.id,
        selection: initialSelection,
        savedSelection: initialSelection,
        customText: initialCustomText,
        savedCustomText: initialCustomText,
      }),
    );
  }, [initialSelection, offer]);

  const requestDraft = useMemo<ChangeOfferDocumentProfileRequest>(
    () =>
      buildPestsRequest({
        offerId: offer?.id ?? offerId ?? "",
        selection,
        savedSelection,
        customText,
        savedCustomText,
      }),
    [
      customText,
      offer?.id,
      offerId,
      savedCustomText,
      savedSelection,
      selection,
    ],
  );

  const hasPendingChanges = useMemo(() => {
    if (!savedRequest) {
      return false;
    }

    return JSON.stringify(savedRequest) !== JSON.stringify(requestDraft);
  }, [requestDraft, savedRequest]);

  const onDebouncedChangeDocumentProfile = useCallback(
    (nextRequest: ChangeOfferDocumentProfileRequest) => {
      lastSubmittedRequest.current = nextRequest;
      changeDocumentProfile(nextRequest);
    },
    [changeDocumentProfile],
  );

  useDebouncedMutationWithPersistenceStateContextUpdate(
    requestDraft,
    onDebouncedChangeDocumentProfile,
    isSavingDocumentProfile,
    changeDocumentProfileError,
    isChangeDocumentProfileSuccess,
    resetChangeDocumentProfile,
    500,
    {
      skip: !offer || offer.immutable || !hasPendingChanges,
      toastError: true,
    },
  );

  useEffect(() => {
    if (
      !changeDocumentProfileError ||
      !rtkErrIsValidationError(changeDocumentProfileError)
    ) {
      return;
    }

    setValidationError(changeDocumentProfileError.data as ValidationError);
  }, [changeDocumentProfileError, setValidationError]);

  useEffect(() => {
    if (!isChangeDocumentProfileSuccess || !offer) {
      return;
    }

    if (lastSubmittedRequest.current) {
      setSavedRequest(lastSubmittedRequest.current);
      const submittedSelection =
        lastSubmittedRequest.current.targetOrganismSelection;
      if (submittedSelection) {
        setSavedSelection(
          normalizeTargetOrganismSelectionValue({
            categoryKeys: submittedSelection.categoryKeys ?? [],
            typeKeys: submittedSelection.typeKeys ?? [],
          }),
        );
        setSavedCustomText(submittedSelection.customText ?? "");
      }
    }

    reloadSnapshot({ offerId: offer.id });
    setValidationError(null);
  }, [
    isChangeDocumentProfileSuccess,
    offer,
    reloadSnapshot,
    setValidationError,
  ]);

  const onSelectionChange = useCallback(
    (next: TargetOrganismSelectionValue) => {
      const normalized = normalizeTargetOrganismSelectionValue(next);
      setSelection((current) => {
        if (isSameTargetOrganismSelection(current, normalized)) {
          return current;
        }
        return normalized;
      });
    },
    [],
  );

  if (error) {
    return <RTKQueryErrorAlert error={error} />;
  }

  if (isLoading) {
    return (
      <Card className="text-muted-foreground animate-pulse p-6">
        {t("Lade Daten...")}
      </Card>
    );
  }

  if (!offer) {
    return null;
  }

  return (
    <Card>
      <CardHeader className="py-4">
        <CardTitle>{t("Schädlingsauswahl")}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid content-start gap-1.5">
          <Label className="min-h-5 text-sm">{t("Schädlinge")}</Label>
          <TargetOrganismSelectionEditor
            selection={selection}
            onChange={onSelectionChange}
            disabled={offer.immutable}
            categoryOptions={categoryOptions}
            typeOptions={typeOptions}
          />
        </div>

        <div className="grid content-start gap-1.5">
          <Label className="min-h-5 text-sm">{t("Zielorganismen Text")}</Label>
          <Textarea
            value={customText}
            disabled={offer.immutable}
            onChange={(event) => {
              setCustomText(event.currentTarget.value);
            }}
            placeholder={t("Optionaler individueller Text für Zielorganismen")}
          />
        </div>

        <RTKQueryErrorAlert error={changeDocumentProfileError} />
      </CardContent>
    </Card>
  );
}
